/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { useEffect, useState } from "react";

import docAPI from "api";
import ServiceWorkerHandler from "./serviceworker/ServiceWorkerHandler";

import { trackLiquidModeNetworkProxyReady } from "analytics/events";
import { liquidModeNetworkProxyTechnology } from "constants/enums";

const setUpServiceWorker = serviceWorkerScope =>
  new Promise((resolve, reject) => {
    const swHandler = ServiceWorkerHandler.getInstance({
      fetchDataCallback: (...args) => docAPI.getLiquidModeViewResource(...args),
      serviceWorkerScope
    });
    swHandler.setUpServiceWorker().then(
      e => {
        resolve(e);
      },
      e => {
        reject(e);
      }
    );
  });

const useNetworkProxy = (serviceWorkerScope, networkProxyTechnology) => {
  const [networkProxyReady, setNetworkProxyReady] = useState(false);

  useEffect(() => {
    if (!networkProxyReady) {
      if (networkProxyTechnology === liquidModeNetworkProxyTechnology.SERVICE_WORKERS) {
        setUpServiceWorker(serviceWorkerScope)
          .then(statusObj => {
            const { status } = statusObj;
            setNetworkProxyReady(status);
          })
          .catch(e => {
            const { status, errorMsg } = e;
            setNetworkProxyReady(status);
            console.error(`ServiceWorker set-up failed - ${errorMsg}`);
          });
      }
    }

    return () => {
      ServiceWorkerHandler.reset();
    };
  }, [networkProxyTechnology]);

  return { networkProxyReady };
};

const NetworkProxy = props => {
  const { serviceWorkerScope, networkProxyTechnology, setNetworkProxyReady } = props;
  const { networkProxyReady } = useNetworkProxy(serviceWorkerScope, networkProxyTechnology);

  useEffect(() => {
    if (networkProxyReady) {
      setNetworkProxyReady(networkProxyReady);
      trackLiquidModeNetworkProxyReady(networkProxyTechnology);
    }
  }, [networkProxyTechnology, networkProxyReady]);
  return null;
};

export default NetworkProxy;
